import { useCallback } from 'react';
import {
  useNavigate,
  createPath,
  resolvePath,
  type To,
  type NavigateOptions
} from 'react-router-dom';
import { getLatestLocation } from './navigationTracker';

const FALLBACK_DELAY_MS = 120;

interface SafeNavigateOptions extends NavigateOptions {
  replace?: boolean;
  disableSyncFallback?: boolean;
}

export const useSafeNavigate = () => {
  const navigate = useNavigate();

  return useCallback((to: To, options?: SafeNavigateOptions) => {
    const latestLocation = getLatestLocation();
    const basePath = latestLocation?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
    const resolvedDescriptor = resolvePath(to, basePath);
    const expectedPath = createPath(resolvedDescriptor);

    navigate(to, options);

    if (typeof window === 'undefined' || options?.disableSyncFallback) {
      return;
    }

    window.setTimeout(() => {
      const updatedLocation = getLatestLocation();
      if (!updatedLocation) {
        return;
      }

      const routerUrl = `${updatedLocation.pathname}${updatedLocation.search}${updatedLocation.hash}`;
      const windowUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      if (routerUrl === windowUrl) {
        return;
      }

      if (windowUrl !== expectedPath) {
        return;
      }

      console.warn('[Navigation Sync] Router desynced; forcing reload:', expectedPath);
      window.location.assign(`${window.location.origin}${expectedPath}`);
    }, FALLBACK_DELAY_MS);
  }, [navigate]);
};
